import React, { useEffect, useState } from 'react';
import { supabase } from './supabaseClient';
import { syncFileSession } from './adminAuth';
import './adminPasswordChange.css';

function Eye({ visible }) {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    {visible ? <><path d="m3 3 18 18M10.6 10.6a2 2 0 0 0 2.8 2.8"/><path d="M9.8 5.2A11 11 0 0 1 12 5c6 0 10 7 10 7a20 20 0 0 1-3.1 3.8M6.2 6.2C3.6 8.1 2 12 2 12s4 7 10 7a11 11 0 0 0 5.8-1.8"/></> : <><path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Z"/><circle cx="12" cy="12" r="3"/></>}
  </svg>;
}
export default function AdminPasswordChange({ workspace, user, onSuccess, onLogout }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [branding, setBranding] = useState(null);
  const [logoFailed, setLogoFailed] = useState(false);
  useEffect(() => {
    let disposed = false;
    async function loadBranding() {
      try {
        const { data } = await supabase.auth.getSession();
        if (!data?.session?.access_token) return;
        const response = await fetch('/api/admin-password-context', { cache: 'no-store', headers: { Authorization: `Bearer ${data.session.access_token}` } });
        const result = await response.json();
        if (response.ok && typeof result?.companyName === 'string' && !disposed) {
          setBranding({ companyName: result.companyName, logo: /^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/.test(result.logo || '') ? result.logo : null });
        }
      } catch (_) { /* Existing workspace branding remains usable if optional context is unavailable. */ }
    }
    setBranding(null); setLogoFailed(false); loadBranding();
    return () => { disposed = true; };
  }, [user?.userId, user?.companyId]);
  const companyName = branding?.companyName || (workspace?.kind === 'company' ? workspace.companyName : null) || 'TrustWork';
  const logo = branding?.logo || (workspace?.logo === '/api/workspace-logo' ? workspace.logo : null);
  useEffect(() => { setLogoFailed(false); }, [logo]);
  const longEnough = Array.from(password).length >= 12;
  const withinLimit = new Blob([password]).size <= 72;
  const matches = Boolean(confirm) && password === confirm;
  async function submit(event) {
    event.preventDefault(); setError('');
    if (!longEnough) { setError('Use at least 12 characters for your new password.'); return; }
    if (!withinLimit) { setError('Your password is too long. Use up to 72 characters; accented letters and emoji may reach the limit sooner.'); return; }
    if (!matches) { setError('The passwords do not match. Enter the same password in both fields.'); return; }
    setBusy(true);
    try {
      const { error: changeError } = await supabase.auth.updateUser({ password });
      if (changeError) throw changeError;
      const { data, error: completionError } = await supabase.rpc('admin_complete_password_change');
      if (completionError) throw completionError;
      if (data?.passwordChangeRequired !== false) throw new Error('Could not confirm the password change.');
      await syncFileSession();
      onSuccess(data);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }
  return <main className="admin-password-page">
    <div className="admin-password-shell">
      <aside className="admin-password-brand" aria-label="Your workspace">
        <div>
          {logo && !logoFailed && <div className="admin-password-logo"><img src={logo} alt={`${companyName} logo`} onError={() => setLogoFailed(true)}/></div>}
          <p className="admin-password-eyebrow">YOUR WORKSPACE</p>
          <h1>{companyName}</h1>
          <p className="admin-password-brand-sub">People, payroll and everything that moves your team forward.</p>
        </div>
        <div className="admin-password-account">
          <span className="admin-password-account-label">SIGNED IN AS</span>
          {user?.login_username && <strong>{user.login_username}</strong>}
          <span>{user?.role === 'admin' ? 'Super Administrator' : 'Company Administrator · HR & Payroll'}</span>
        </div>
        <div className="admin-password-brand-footer"><span className="admin-password-status-dot"/>Your account. Your own password.</div>
      </aside>
      <section className="admin-password-form-panel" aria-labelledby="admin-password-title">
        <span className="admin-password-step">FIRST-TIME SETUP</span>
        <div className="admin-password-lock" aria-hidden="true"><svg width="25" height="25" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><rect x="5" y="10" width="14" height="11" rx="3"/><path d="M8 10V7a4 4 0 0 1 8 0v3M12 14v3"/></svg></div>
        <h2 id="admin-password-title">Set your password</h2>
        <p className="admin-password-intro">Welcome aboard. Replace your temporary password to open your workspace.</p>
        <form onSubmit={submit} aria-busy={busy}>
          <div className="admin-password-field">
            <label htmlFor="admin-new-password">New password <span aria-hidden="true">*</span></label>
            <div className="admin-password-input-wrap">
              <input id="admin-new-password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" value={password} onChange={e => setPassword(e.target.value)} required minLength={12} maxLength={72} disabled={busy} aria-describedby="admin-password-rules"/>
              <button type="button" className="admin-password-eye" aria-label={showPassword ? 'Hide new password' : 'Show new password'} aria-pressed={showPassword} onClick={() => setShowPassword(value => !value)} disabled={busy}><Eye visible={showPassword}/></button>
            </div>
          </div>
          <div className="admin-password-field">
            <label htmlFor="admin-confirm-password">Confirm password <span aria-hidden="true">*</span></label>
            <div className="admin-password-input-wrap">
              <input id="admin-confirm-password" type={showConfirm ? 'text' : 'password'} autoComplete="new-password" value={confirm} onChange={e => setConfirm(e.target.value)} required minLength={12} maxLength={72} disabled={busy} aria-describedby={confirm ? 'admin-password-match' : undefined} aria-invalid={confirm ? !matches : undefined}/>
              <button type="button" className="admin-password-eye" aria-label={showConfirm ? 'Hide confirm password' : 'Show confirm password'} aria-pressed={showConfirm} onClick={() => setShowConfirm(value => !value)} disabled={busy}><Eye visible={showConfirm}/></button>
            </div>
            {confirm && <p id="admin-password-match" className={`admin-password-match ${matches ? 'is-matched' : ''}`} role="status">{matches ? 'Passwords match' : 'Passwords do not match yet'}</p>}
          </div>
          <div className="admin-password-rules" id="admin-password-rules">
            <strong>Password requirements</strong>
            <ul>
              <li data-met={longEnough}>At least 12 characters</li>
              <li data-met={Boolean(password) && withinLimit}>Up to 72 characters; accented letters and emoji may reach the limit sooner</li>
              <li data-met={matches}>Both passwords must match</li>
              <li>Choose a different password from your temporary one</li>
            </ul>
            <p>A few memorable words work well. Any additional requirements from your sign-in settings will appear if needed.</p>
          </div>
          {error && <p className="admin-password-error" role="alert">{error}</p>}
          <button type="submit" className="admin-password-save" disabled={busy}>{busy ? 'Saving your password…' : 'Save password & continue'}{!busy && <span aria-hidden="true">→</span>}</button>
          <button className="admin-password-signout" type="button" onClick={onLogout} disabled={busy}>Sign out</button>
        </form>
        <p className="admin-password-powered">Powered by <strong>TrustWork</strong></p>
      </section>
    </div>
  </main>;
}
